import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import Select from '../components/Select';
import {
  formatarProducaoCurta, labelUnidadePlural, unidadeArea,
} from '../utils/unidades';
import Metrica, { MetricaGrid } from '../components/ui/Metrica';
import EmptyState from '../components/ui/EmptyState';
import Estado from '../components/ui/Estado';
import Field from '../components/ui/Field';
import PageHeader from '../components/ui/PageHeader';
import DataTable from '../components/ui/DataTable';
import DonutChart from '../components/ui/DonutChart';
import BarChart from '../components/ui/BarChart';
import { fmt, fmtBRL, fmtPct } from '../utils/format';
import { labelCategoria } from '../utils/categorias';
import {
  CORES_GRAFICO, COR_RECEITA, COR_CUSTO, classeSinal, tomSinal,
} from '../utils/graficos';

/** Aviso de safra com produção em unidades diferentes. */
function AvisoMisto({ producaoPorUnidade }) {
  if (!producaoPorUnidade || producaoPorUnidade.length < 2) return null;
  return (
    <div className="aviso aviso--atencao">
      <strong>Produção em unidades diferentes.</strong> Quantidades de unidades distintas
      não podem ser somadas, então cada uma aparece separada. Os valores em reais
      consideram tudo.
    </div>
  );
}

/** Lista de produção por unidade, uma linha por unidade. */
function ProducaoPorUnidade({ itens }) {
  if (!itens?.length) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' }}>
      {itens.map(p => (
        <div key={p.unidade} style={{
          background: 'var(--bg)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', padding: '0.85rem', textAlign: 'center',
        }}>
          <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.3rem' }}>
            {labelUnidadePlural(p.unidade)}
          </div>
          <div style={{ fontSize: '1.05rem', fontWeight: 700 }}>{fmt(p.total)}</div>
          {p.totalSacos != null && (
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
              {fmt(p.totalSacos)} sacos
            </div>
          )}
          {p.registros != null && p.registros > 0 && (
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
              {p.registros} registro{p.registros > 1 ? 's' : ''}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Resultado() {
  const [safras, setSafras] = useState([]);
  const [safraId, setSafraId] = useState('');
  const [resultado, setResultado] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [historico, setHistorico] = useState(null);
  const [erroSafras, setErroSafras] = useState('');
  const [erroResultado, setErroResultado] = useState('');
  const [erroHistorico, setErroHistorico] = useState('');

  // Falha ao buscar as safras deixava o seletor vazio, como se não houvesse
  // safra cadastrada. Agora o erro aparece e dá para tentar de novo.
  const carregarSafras = useCallback(() => {
    setErroSafras('');
    api.getSafras()
      .then(s => setSafras(s || []))
      .catch(() => setErroSafras('Não conseguimos buscar suas safras agora. Confira a internet e tente de novo.'));
  }, []);

  useEffect(() => { carregarSafras(); }, [carregarSafras]);

  const carregarHistorico = useCallback(() => {
    setErroHistorico('');
    api.getHistoricoProdutividade()
      .then(setHistorico)
      .catch(() => setErroHistorico('Não conseguimos montar o histórico de produtividade agora.'));
  }, []);

  useEffect(() => { carregarHistorico(); }, [carregarHistorico]);

  // A guarda evita que a resposta de uma safra trocada às pressas chegue depois
  // e apareça no lugar da safra escolhida agora.
  const carregarResultado = useCallback(() => {
    if (!safraId) { setResultado(null); setErroResultado(''); setCarregando(false); return undefined; }
    let vivo = true;
    setCarregando(true);
    setErroResultado('');
    api.getResultadoSafra(safraId)
      .then(r => { if (vivo) setResultado(r); })
      .catch(() => {
        if (!vivo) return;
        setResultado(null);
        setErroResultado('Não conseguimos calcular o resultado desta safra agora. Confira a internet e tente de novo. Seus dados continuam salvos.');
      })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [safraId]);

  useEffect(() => carregarResultado(), [carregarResultado]);

  const tomResultado = resultado ? tomSinal(resultado.resultadoBrutoBRL) : undefined;
  const classeResultado = resultado ? classeSinal(resultado.resultadoBrutoBRL) : undefined;
  const lavourasComValor = resultado?.lavouras?.filter(
    l => (l.receitaEstimadaBRL || 0) > 0 || (l.custoTotalBRL || 0) > 0
  ) || [];

  return (
    <div>
      <PageHeader
        titulo="Resultado"
        icone="resultado"
        descricao="Lucro, custos e produtividade da safra"
      >
        <Estado erro={erroSafras} aoTentarNovamente={carregarSafras}>
          <div className="filtros">
            <Field label="Safra / ciclo">
              <Select
                value={safraId}
                onChange={setSafraId}
                options={safras.map(s => ({ value: s.id, label: `${s.cultura || 'Safra'} - ${s.descricao}` }))}
                placeholder="Selecione a safra"
              />
            </Field>
          </div>
        </Estado>
      </PageHeader>

      {erroResultado && (
        <Estado erro={erroResultado} aoTentarNovamente={carregarResultado} />
      )}

      {!safraId && !carregando && !erroSafras && (
        <EmptyState
          icone="resultado"
          titulo="Escolha uma safra"
          descricao="Selecione a safra acima para ver o resultado financeiro, produção por unidade e o desempenho de cada área de cultivo."
        />
      )}

      {carregando && <Estado carregando itens={4} />}

      {resultado && !carregando && (
        <>
          <MetricaGrid min={160}>
            <Metrica
              label="Total vendido"
              value={fmtBRL(resultado.receitaBrutaBRL)}
              destaque
              tom="receita"
              dica="Soma de todas as vendas registradas nesta safra."
            />
            <Metrica
              label="Custos"
              value={fmtBRL(resultado.custoTotalInsumosBRL)}
              destaque
              tom="custo"
              dica="Insumos, mão de obra e serviços lançados nesta safra."
            />
            <Metrica
              label="Lucro bruto"
              value={fmtBRL(resultado.resultadoBrutoBRL)}
              destaque
              tom={tomResultado}
              dica="Total vendido menos os custos."
            />
            <Metrica
              label="Margem"
              value={resultado.receitaBrutaBRL > 0 ? fmtPct(resultado.margemBrutaPct) : '—'}
              destaque
              tom={tomResultado}
              dica="Quanto do que você vendeu virou lucro."
            />
          </MetricaGrid>

          <div className="dre">
            <div className="dre__linha">
              <span>Total vendido</span>
              <span className="valor-positivo">{fmtBRL(resultado.receitaBrutaBRL)}</span>
            </div>
            <div className="dre__linha">
              <span style={{ paddingLeft: '1rem', color: 'var(--text-muted)' }}>(−) Custo de insumos</span>
              <span className="valor-negativo">({fmtBRL(resultado.custoTotalInsumosBRL)})</span>
            </div>
            <div className="dre__linha dre__linha--total">
              <span>Lucro bruto</span>
              <span className={classeResultado}>{fmtBRL(resultado.resultadoBrutoBRL)}</span>
            </div>
          </div>

          {(resultado.custosPorCategoria?.length > 0 || lavourasComValor.length > 0) && (
            <div className="grade-graficos">
              {resultado.custosPorCategoria?.length > 0 && (
                <div className="card">
                  <h2>Custos por categoria</h2>
                  <DonutChart
                    data={resultado.custosPorCategoria.map(c => ({
                      label: labelCategoria(c.categoria),
                      value: c.custoBRL,
                    }))}
                    centro={fmtBRL(resultado.custoTotalInsumosBRL)}
                    centroSub="custo total"
                  />
                  <DataTable
                    linhas={resultado.custosPorCategoria}
                    chaveDe={c => c.categoria}
                    colunas={[
                      { chave: 'categoria', titulo: 'Categoria', papel: 'titulo',
                        render: c => labelCategoria(c.categoria) },
                      { chave: 'custo', titulo: 'Custo', alinhar: 'direita', papel: 'destaque',
                        render: c => fmtBRL(c.custoBRL) },
                      { chave: 'pct', titulo: '% do total', render: c => (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span>{fmt(c.pctDoTotal)}%</span>
                            <div style={{ flex: 1, background: 'var(--border)', borderRadius: '99px', height: '8px', overflow: 'hidden', minWidth: '60px' }}>
                              <div style={{ width: `${Math.min(c.pctDoTotal, 100)}%`, background: 'var(--accent)', height: '100%', borderRadius: '99px' }} />
                            </div>
                          </div>
                        ) },
                    ]}
                  />
                </div>
              )}
              {lavourasComValor.length > 0 && (
                <div className="card">
                  <h2>Receita vs custo</h2>
                  <BarChart
                    showSecond
                    height={36}
                    labelWidth={110}
                    formatValue={fmtBRL}
                    legenda={[
                      { cor: COR_RECEITA, texto: 'Receita' },
                      { cor: COR_CUSTO, texto: 'Custos' },
                    ]}
                    data={lavourasComValor.slice(0, 10).map(l => ({
                      label: l.lavouraDescricao,
                      value: l.receitaEstimadaBRL || 0,
                      color: COR_RECEITA,
                      secondValue: l.custoTotalBRL || 0,
                      secondColor: COR_CUSTO,
                    }))}
                  />
                </div>
              )}
            </div>
          )}

          <div className="card">
            <h2>Produção e estoque</h2>
            <AvisoMisto producaoPorUnidade={resultado.producaoPorUnidade} />

            {resultado.producaoPorUnidade?.length > 0 ? (
              <>
                <h3 style={{ fontSize: '0.8rem', marginBottom: '0.6rem' }}>Produzido</h3>
                <ProducaoPorUnidade itens={resultado.producaoPorUnidade} />
              </>
            ) : (
              <EmptyState
                compacto
                icone="colheita"
                titulo="Nenhuma produção registrada"
                descricao="Registre a colheita em Produção / Cargas para ver quanto foi produzido e o custo por unidade."
                acao={{ to: '/cargas', label: 'Registrar produção' }}
              />
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', marginTop: '1.25rem' }}>
              {/* Saca só aparece quando a produção tem peso conhecido. */}
              {resultado.totalSacosProduzidos > 0 && (() => {
                const kgVendido = resultado.totalKgVendido || 0;
                const kgEstoque = (resultado.totalKgProduzido || 0) - kgVendido;
                const sacasVendidas = resultado.totalSacosVendidos || 0;
                const sacasEstoque = resultado.saldoSacosEstoque || 0;
                return (
                  <>
                    <Metrica
                      label="Vendido"
                      value={kgVendido > 0 ? `${fmt(kgVendido)} kg` : '—'}
                      detalhe={sacasVendidas > 0 ? `${fmt(sacasVendidas)} sacos` : undefined}
                    />
                    <Metrica
                      label="Ainda em estoque"
                      value={kgEstoque !== 0 ? `${fmt(kgEstoque)} kg` : '—'}
                      detalhe={sacasEstoque !== 0 ? `${fmt(sacasEstoque)} sacos` : undefined}
                      tom={kgEstoque > 0 ? 'positivo' : kgEstoque < 0 ? 'negativo' : undefined}
                      destaque
                    />
                    <Metrica
                      label="Preço médio de venda"
                      value={resultado.precoMedioSacoBRL > 0 ? fmtBRL(resultado.precoMedioSacoBRL / 60) + '/kg' : '—'}
                      detalhe={resultado.precoMedioSacoBRL > 0 ? `${fmtBRL(resultado.precoMedioSacoBRL)}/saco` : undefined}
                    />
                  </>
                );
              })()}
              {resultado.custoPorUnidadeBRL != null && (
                <Metrica
                  label={`Custo / ${labelUnidadePlural(resultado.unidadeProducao).replace(/s$/, '')}`}
                  value={fmtBRL(resultado.custoPorUnidadeBRL)}
                />
              )}
              {resultado.resultadoPorUnidadeBRL != null && (
                <Metrica
                  label={`Lucro / ${labelUnidadePlural(resultado.unidadeProducao).replace(/s$/, '')}`}
                  value={fmtBRL(resultado.resultadoPorUnidadeBRL)}
                  tom={tomSinal(resultado.resultadoPorUnidadeBRL)}
                />
              )}
              {resultado.valorEstimadoEstoqueBRL > 0 && (
                <Metrica
                  label="Valor em estoque"
                  value={fmtBRL(resultado.valorEstimadoEstoqueBRL)}
                  detalhe="estoque × preço médio de venda"
                  tom="receita"
                  destaque
                />
              )}
            </div>
          </div>

          {resultado.lavouras?.length > 0 && (
            <div className="card">
              <h2>Resultado por área de cultivo</h2>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                Receita estimada proporcional à produção de cada área. Cada linha mostra a
                produção na unidade em que foi registrada.
              </p>
              {resultado.lavouras.some(l => l.removidaDaSafra) && (
                <p className="aviso aviso--atencao">
                  Áreas marcadas como <strong>removida da safra</strong> não fazem mais parte
                  dela, mas o que já foi gasto e produzido continua somando no total da safra.
                  Indicadores por área ficam em branco nessas linhas.
                </p>
              )}
              {/* Nenhuma coluna usa `ocultarSeVazia`: o rodapé é posicional, e uma
                  coluna que desaparece deslocaria os totais para a coluna errada. */}
              <DataTable
                linhas={resultado.lavouras}
                chaveDe={l => l.safraLavouraId}
                colunas={[
                  { chave: 'area', titulo: 'Área de cultivo', papel: 'titulo', render: l => {
                      const estufa = l.tipoArea === 'ESTUFA';
                      return (
                        <span style={{ fontWeight: 600 }}>
                          {estufa ? 'Estufa' : 'Lavoura'} - {l.lavouraDescricao}
                          {l.removidaDaSafra && (
                            <span className="selo selo--alerta" style={{ marginLeft: '0.4rem' }}>
                              removida da safra
                            </span>
                          )}
                        </span>
                      );
                    } },
                  { chave: 'cultura', titulo: 'Cultura / variedade', papel: 'subtitulo',
                    render: l => `${l.tipoGrao}${l.variedade ? ` · ${l.variedade}` : ''}` },
                  { chave: 'tamanho', titulo: 'Tamanho',
                    render: l => (l.areaHectares != null ? `${fmt(l.areaHectares)} ${unidadeArea(l.tipoArea)}` : '—') },
                  { chave: 'producao', titulo: 'Produção',
                    render: l => formatarProducaoCurta(l.totalProduzidoUnidade, l.unidadeProducao) },
                  { chave: 'custos', titulo: 'Custos', render: l => fmtBRL(l.custoTotalBRL) },
                  { chave: 'receita', titulo: 'Receita est.', render: l => (
                      <span className="valor-positivo">
                        {l.receitaEstimadaBRL != null ? fmtBRL(l.receitaEstimadaBRL) : '—'}
                      </span>
                    ) },
                  { chave: 'lucro', titulo: 'Lucro', papel: 'destaque', render: l => (
                      <span className={classeSinal(l.resultadoBRL)} style={{ fontWeight: 700 }}>
                        {l.resultadoBRL != null ? fmtBRL(l.resultadoBRL) : '—'}
                      </span>
                    ) },
                  { chave: 'lucroArea', titulo: 'Lucro / área', render: l => (
                      <span className={classeSinal(l.resultadoBRL)}>
                        {l.tipoArea === 'ESTUFA'
                          ? (l.resultadoBRL != null ? fmtBRL(l.resultadoBRL) : '—')
                          : (l.resultadoPorHectareBRL != null ? fmtBRL(l.resultadoPorHectareBRL) : '—')}
                      </span>
                    ) },
                ]}
                rodape={[
                  <strong key="total">Total</strong>,
                  '',
                  '',
                  '',
                  <strong key="custos">{fmtBRL(resultado.custoTotalInsumosBRL)}</strong>,
                  <strong key="receita" className="valor-positivo">{fmtBRL(resultado.receitaBrutaBRL)}</strong>,
                  <strong key="lucro" className={classeResultado}>{fmtBRL(resultado.resultadoBrutoBRL)}</strong>,
                  '',
                ]}
              />
            </div>
          )}

          {resultado.receitaBrutaBRL === 0 && resultado.custoTotalInsumosBRL === 0 && (
            <div className="card">
              <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '1rem' }}>
                Nenhuma venda ou custo registrado para esta safra ainda.
              </p>
            </div>
          )}
        </>
      )}

      {erroHistorico && (
        <div className="aviso aviso--atencao">
          {erroHistorico}{' '}
          <button type="button" onClick={carregarHistorico}
            style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', padding: 0, minHeight: 'unset', fontWeight: 700 }}>
            Tentar novamente
          </button>
        </div>
      )}

      {historico && historico.safras.length >= 1
        && historico.series.some(s => s.sacasPorHectare.some(v => v != null)) && (
        <div className="card">
          <h2>Histórico de produtividade por safra</h2>
          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
            Sacas por hectare em cada área ao longo das safras. Só aparecem áreas com
            produção em kg ou saco e com área informada — caixa e maço não têm peso
            definido, então não geram sacas por hectare.
          </p>

          <div className="grade-graficos">
            {historico.series.map((serie, i) => {
              const dados = historico.safras
                .map((safra, si) => ({
                  label: safra,
                  value: serie.sacasPorHectare[si],
                  color: CORES_GRAFICO[i % CORES_GRAFICO.length],
                }))
                .filter(d => d.value != null);
              if (dados.length === 0) return null;
              return (
                <div key={`${serie.lavoura}-${serie.tipoGrao}`}>
                  <h3 style={{ fontSize: '0.82rem', marginBottom: '0.5rem' }}>
                    {serie.lavoura} · {serie.tipoGrao}
                  </h3>
                  <BarChart
                    data={dados}
                    formatValue={(v) => `${fmt(v)} sc/ha`}
                    height={28}
                    labelWidth={100}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
